import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, Col, Container, Form, FormGroup, Label, Row } from "reactstrap";
import { MetaTags } from "react-meta-tags";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import SfLoadingSkeleton from "components/Common/Loadings/SfLoadingSkeleton";
import BootstrapTable from 'react-bootstrap-table-next';
import { AppConfiguration } from 'config';
import { useDispatch, useSelector } from "react-redux";
import { DEFAULT_TABLE_SORT_BY_CREATED_AT_IN_DESC, REQUIRED_FEATURE_FLAGS } from "helpers/sf_constants";
import filterFactory from 'react-bootstrap-table2-filter';
import { getIANATimezoneStringForAbbreviation } from "helpers/sf_utils";
import { customSortCaret } from "helpers/bootstrap_table_helper";
import { adminCampaignFulFillmentColumns } from "pages/Admin/AdminCampaignFulFillment/admin_campaign_fulfillment_helper";
import { postAdminUserLogin } from "store/admin/user-login/actions";
import CustomDateRangePicker, { DATE_RANGE_OPTIONS, createOption } from "components/Common/DateRangePicker";
import { getClientCampaignFulfillment, exportClientCampaignFulfillment } from "store/campaign-fulfillment/actions";
import ToastAlert, { ToastAlertIconType } from "components/Common/ToastAlert/ToastAlert";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import CampaignBreadcrumb from "../CommonComponents/CampaignBreadcrumb/CampaignBreadcrumb";
import useCampaignsWithStats from "hooks/useCampaignsWithStats";
import useFeatureFlags from "hooks/useFeatureFlags";
import OffsetPagination from "components/Common/OffsetPagination";

const CampaignFulfillment = () => {
    const dispatch = useDispatch();
    const { campaign_id } = useParams();
    const [isFeatureFlagActive] = useFeatureFlags();
    const { current_account } = useSelector((state) => ({
        current_account: state.accounts.current_account,
    }))
    const [campaigns] = useCampaignsWithStats();
    const currentSortRef = useRef({ sort_column: null, sort_direction: null });
    const sortHandlerRef = useRef(null);
    const lastTableDataRef = useRef([]);


    const { client_campaign_fulfillment, client_campaign_fulfillment_loading, export_loading, has_next_page, has_lookahead_pages, total_number_of_pages, last_page_fetched } = useSelector((state) => ({
        client_campaign_fulfillment: state.ClientCampaignFulfillment.client_campaign_fulfillment,
        client_campaign_fulfillment_loading: state.ClientCampaignFulfillment.loading,
        export_loading: state.ClientCampaignFulfillment.export_loading,
        has_next_page: state.ClientCampaignFulfillment.has_next_page,
        has_lookahead_pages: state.ClientCampaignFulfillment.has_lookahead_pages,
        total_number_of_pages: state.ClientCampaignFulfillment.total_number_of_pages,
        last_page_fetched: state.ClientCampaignFulfillment.last_page_fetched,
    }));

    const active_campaign = useMemo(() => {
        return campaigns.find(el => el.id.toString() === campaign_id)
    }, [campaigns, campaign_id])

    const [selectedDateRange, setSelectedDateRange] = useState(
        createOption(
            DATE_RANGE_OPTIONS.LAST_30_DAYS,
            new Date(),
            getIANATimezoneStringForAbbreviation(current_account?.timezone)
        )
    )
    const shouldUseOffsetPagination = useMemo(() => (
        isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_OFFSET_PAGINATION_APPLICATION)
    ), [isFeatureFlagActive])

    const onClickUserLogin = (account_id) => {
        dispatch(postAdminUserLogin({ user: { account: account_id }, history: history }))
    }

    sortHandlerRef.current = (field, order) => {
        currentSortRef.current = { sort_column: field, sort_direction: order };
        const queryParams = buildQueryParams(1);
        if (queryParams) {
            dispatch(getClientCampaignFulfillment({
                queryParams,
                appendResults: false,
                useCache: shouldUseOffsetPagination,
            }, true));
        }
    };

    const columns = useMemo(() => {
        return adminCampaignFulFillmentColumns(onClickUserLogin, true, false);
    }, []);

    // Sorting is remote: the table only emits the sort event and does NOT
    // re-sort the current page client-side. We fetch the sorted page from the
    // backend here (single source of truth).
    const handleTableChange = (type, { sortField, sortOrder }) => {
        if (type !== 'sort') return;
        sortHandlerRef.current(sortField, sortOrder);
    };

    const onChangeDateRange = (value) => {
        setSelectedDateRange(value)
    }

    // Helper function to build query params with filters
    const buildQueryParams = (pageNumber = 1) => {
        if (!selectedDateRange) {
            return null;
        }
        const dateRangeValue = selectedDateRange?.value;
        const receivedValue = {
            startDate: dateRangeValue.startDate instanceof Date
                ? dateRangeValue.startDate.toISOString()
                : dateRangeValue?.startDate,
            endDate: dateRangeValue.endDate instanceof Date
                ? dateRangeValue.endDate.toISOString()
                : dateRangeValue?.endDate
        };

        const filters = {
            received: true,
            received_value: JSON.stringify(receivedValue),
            received_value_label: selectedDateRange?.original_label || selectedDateRange?.label || "CUSTOM_RANGE"
        };

        const { sort_column, sort_direction } = currentSortRef.current;
        return {
            filters: JSON.stringify(filters),
            page_number: pageNumber,
            campaign_id: campaign_id,
            ...(sort_column ? { sort_column, sort_direction: sort_direction || 'desc' } : {}),
        };
    }

    const onClickExportFulfillment = () => {
        if (export_loading) {
            return;
        }
        if (!client_campaign_fulfillment?.length) {
            ToastAlert.fire({ icon: ToastAlertIconType.ERROR, title: 'No data available for the applied filters' });
            return;
        }
        const queryParams = buildQueryParams();
        if (queryParams) {
            dispatch(exportClientCampaignFulfillment({ queryParams }));
        }
    }

    const fetchNextBatchWrapper = (_filterModal = null, last_page_fetched = 0) => {
        if (client_campaign_fulfillment_loading) {
            return;
        }
        const queryParams = buildQueryParams(last_page_fetched + 1);
        if (queryParams) {
            dispatch(getClientCampaignFulfillment({
                queryParams,
                appendResults: last_page_fetched > 0,
                useCache: shouldUseOffsetPagination,
            }, true))
        }
    }

    const onClickLoadMore = () => {
        fetchNextBatchWrapper(null, last_page_fetched);
    }
    const currentPage = Math.max(1, Number(last_page_fetched) || 1)
    const knownTotalPages = Math.max(Number(total_number_of_pages) || 0, currentPage)
    const paginationTotal = shouldUseOffsetPagination
        ? (knownTotalPages > 0
            ? knownTotalPages
            : (has_next_page
                ? currentPage + (has_lookahead_pages ? 8 : 1)
                : currentPage))
        : Math.max(Number(total_number_of_pages) || 0, currentPage)

    const fetchCampaignFulfillmentPage = (page, options = {}) => {
        const parsedPage = Number(page)
        if (!Number.isFinite(parsedPage) || parsedPage < 1 || client_campaign_fulfillment_loading) {
            return
        }
        const queryParams = buildQueryParams(parsedPage)
        if (queryParams) {
            dispatch(getClientCampaignFulfillment({
                queryParams,
                appendResults: Boolean(options.appendResults),
                useCache: shouldUseOffsetPagination,
            }, true))
        }
    }

    useEffect(() => {
        const queryParams = buildQueryParams(1);
        if (queryParams) {
            dispatch(getClientCampaignFulfillment({
                queryParams,
                appendResults: false,
                useCache: shouldUseOffsetPagination,
            }, true));
        }
    }, [dispatch, campaign_id, selectedDateRange, shouldUseOffsetPagination])

    // Keep the last non-empty dataset only while a fetch is in flight so the
    // table doesn't unmount/flash during a re-fetch. Once loading finishes, honor
    // a genuinely empty result so the "no data" indication and empty state show.
    if (client_campaign_fulfillment?.length) {
        lastTableDataRef.current = client_campaign_fulfillment;
    } else if (!client_campaign_fulfillment_loading) {
        lastTableDataRef.current = client_campaign_fulfillment || [];
    }
    const tableData = lastTableDataRef.current;

    return (
        <React.Fragment>
            <div className="page-content" id="page-content-wrapper">
                <MetaTags>
                    <title>Campaign Fulfillment | {AppConfiguration.WEBSITE}</title>
                </MetaTags>
                <Container fluid>
                    <CampaignBreadcrumb
                        title="Fulfillment"
                        campaignId={campaign_id}
                        breadcrumbItem={active_campaign?.name}
                        campaign_type={active_campaign?.product_type?.name}
                    />
                    <Row>
                        <Col md={12}>
                            <Card>
                                <CardBody>
                                    <ToolkitProvider
                                        keyField="id"
                                        columns={columns}
                                        data={tableData}
                                        exportCSV={{
                                            fileName: "CampaignFulfillment.csv",
                                            exportAll: false,
                                        }}
                                    >
                                        {(toolkitProps) => (
                                            <>
                                                <Form>
                                                    <Row className="d-flex align-items-end">
                                                        <Col md={2} xl={2}>
                                                            <FormGroup>
                                                                <Label htmlFor="lead">Timeframe</Label>
                                                                <CustomDateRangePicker
                                                                    ianaTz={getIANATimezoneStringForAbbreviation(
                                                                        current_account?.timezone
                                                                    )}
                                                                    value={selectedDateRange}
                                                                    options={Object.keys(
                                                                        DATE_RANGE_OPTIONS
                                                                    ).map(opt =>
                                                                        createOption(
                                                                            DATE_RANGE_OPTIONS[opt],
                                                                            new Date(),
                                                                            getIANATimezoneStringForAbbreviation(
                                                                                current_account?.timezone
                                                                            )
                                                                        )
                                                                    )}
                                                                    onChange={onChangeDateRange}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md="auto" className="align-self-end ms-auto">
                                                            <FormGroup className="mt-1 mb-0">
                                                                <div>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-sfblue"
                                                                        disabled={export_loading || !client_campaign_fulfillment?.length}
                                                                        onClick={onClickExportFulfillment}
                                                                    >
                                                                        <i className="bx bx-import font-size-14 align-middle me-2" />
                                                                        {export_loading ? 'Queuing...' : 'Export'}
                                                                    </button>
                                                                </div>
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                </Form>
                                                <Row>
                                                    <Col xl="12">
                                                        <div className="sfTable-Container">
                                                            {(client_campaign_fulfillment_loading && !tableData.length) ? (<SfLoadingSkeleton rows={8} cols={6} />) :
                                                                (<BootstrapTable
                                                                    keyField="id"
                                                                    columns={columns}
                                                                    data={tableData}
                                                                    responsive
                                                                    bordered={false}
                                                                    striped={false}
                                                                    defaultSorted={DEFAULT_TABLE_SORT_BY_CREATED_AT_IN_DESC}
                                                                    rowClasses="tr-hover"
                                                                    filter={filterFactory()}
                                                                    headerClasses="sftableHeader text-black thead-h50"
                                                                    classes="table align-middle table-nowrap text-black"
                                                                    headerWrapperClasses="thead-light"
                                                                    noDataIndication={`You currently don't have any campaign fulfillment data`}
                                                                    sort={{ sortCaret: customSortCaret }}
                                                                    remote={{ sort: true }}
                                                                    onTableChange={handleTableChange}
                                                                    {...toolkitProps.baseProps}
                                                                />)}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                    </ToolkitProvider>
                                    {shouldUseOffsetPagination ? (
                                        <OffsetPagination
                                            currentPage={currentPage}
                                            totalPages={paginationTotal}
                                            hasNextPage={has_next_page}
                                            hasDeepOffsetPages={has_lookahead_pages}
                                            offsetLookAheadPages={8}
                                            loading={client_campaign_fulfillment_loading}
                                            onPageChange={(p) => fetchCampaignFulfillmentPage(p)}
                                            className="campaign-analysis-pagination mt-3 align-items-center justify-content-end"
                                        />
                                    ) : (
                                        <Col xl="12">
                                            <div className="float-end">
                                                <p className="mx-3">
                                                    {has_next_page && (
                                                        <a className='btn btn-outline-sfblue'
                                                            onClick={onClickLoadMore}>
                                                            {client_campaign_fulfillment_loading ? 'Please wait...' : 'Load More'}
                                                        </a>
                                                    )
                                                    }
                                                </p>
                                            </div>
                                        </Col>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {/* Table ends */}
                </Container>
            </div>
        </React.Fragment>
    )
}

export default CampaignFulfillment
